/** @jsxImportSource @emotion/react */
import './theme.scss';
import ReactAutosuggest, {
  AutosuggestProps,
  AutosuggestPropsSingleSection,
  BlurEvent,
  InputProps,
  RenderInputComponent,
} from 'react-autosuggest';
import React, {
  ChangeEvent,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { LinkProps, useHref } from 'react-router-dom';
import { css, jsx } from '@emotion/react';
import Textfield from 'components/textfield';
import theme from './theme';

type ReactAutosuggestProps<TSuggestion, TSection> = AutosuggestProps<
  TSuggestion,
  TSection
>;

type Props<TSuggestion, TSection> = Omit<
  AutosuggestPropsSingleSection<TSuggestion>,
  'inputProps' | 'onSuggestionsFetchRequested' | 'onSuggestionsClearRequested'
> &
  Omit<
    React.HTMLProps<HTMLTextAreaElement | HTMLInputElement>,
    'onChange' | 'value' | 'onBlur'
  > & {
    onChange?:
      | ((event: ChangeEvent<HTMLInputElement>) => void)
      | ((event: ChangeEvent<HTMLTextAreaElement>) => void)
      | ((newValue: string) => void);
    onBlur?(
      event: React.FocusEvent<any>,
      params?: BlurEvent<TSuggestion>
    ): void;
    value?: string;
    autoFocus?: boolean;
    containerWidth?: string;
  };

const renderInputComponentDefault = (inputProps: any) => <Textfield {...inputProps} />;

const Autosuggest = <TSuggestion extends any, TSection extends any>({
  ...props
}: Props<TSuggestion, TSection>) => {
  const { value, onChange, suggestions, containerWidth, getSuggestionValue, onSuggestionSelected, renderSuggestion, renderInputComponent, ...other } = props;
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputProps = {
    ...other,
    autoComplete: 'off',
    value: value || '',
    onChange: (event: React.ChangeEvent, params: any) => {
      const { newValue, method } = params;
      if (onChange && !method.match(/up|down/)) {
        onChange(newValue);
        event.preventDefault();
      }
    },
  };
  const handleSuggestionsClearRequested = useCallback(() => {
    setShowSuggestions(false);
  }, []);

  const handleSuggestionsFetchRequested = useCallback(() => {
    setShowSuggestions(true);
  }, []);

  return (
    <span
      css={css`
        .react-autosuggest__suggestions-container--open {
          width: ${containerWidth || '100%'};
        }
      `}
    >
      {/* @ts-ignore */}
      <ReactAutosuggest
        // @ts-ignore
        inputProps={inputProps}
        {...other}
        renderInputComponent={renderInputComponent || renderInputComponentDefault}
        renderSuggestion={renderSuggestion}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        suggestions={showSuggestions ? suggestions : []}
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
      />
    </span>
  );
};

export default React.memo(Autosuggest) as typeof Autosuggest;
